/**
 * CustomSubscriptionsTab — iter87.85.
 *
 * Per-league custom-integration grants. Sits between Rivalry Subscriptions
 * and Pro+ Subscriptions in the Site Admin nav. Replaces the retired
 * tenant-defaults auto-apply flow.
 *
 * A "custom subscription" is a per-league grant that unlocks integration
 * features:
 *   - GotSport Sync
 *   - CSV Upload
 *   - Portfolio Draft
 *
 * Master switch `custom_enabled` gates whether the 3 integration toggles
 * are editable. Flipping the master off force-offs all 3 (backend
 * enforces the invariant on PATCH).
 *
 * Search by name / invite code / admin email → toggle grants inline.
 */
import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import {
    Search, Loader2, ExternalLink, Trophy, Sparkles,
    Check, Star, Crown,
} from "lucide-react";
import { api } from "@/lib/api";

const INTEGRATIONS = [
    { key: "gotsport_enabled",        label: "GotSport Sync",   help: "Auto-sync scores from GotSport" },
    { key: "feed_csv_enabled",        label: "CSV Upload",       help: "Custom tournament CSV import" },
    { key: "portfolio_mode_unlocked", label: "Portfolio Draft",  help: "Portfolio-mode draft variant" },
];

function CustomLeagueRow({ league, onChange }) {
    const [saving, setSaving] = useState(false);
    const [local, setLocal] = useState(league);

    const patch = async (delta) => {
        setSaving(true);
        try {
            const r = await api.siteSetCustomFeatures(league.id, delta);
            setLocal({ ...local, ...r });
            onChange && onChange(r);
            toast.success("Custom grant updated");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Update failed");
        }
        setSaving(false);
    };

    const isCustom = !!local.custom_enabled;

    return (
        <div
            className={`brutal-card-light p-4 transition-colors ${
                isCustom ? "border-cyber/40" : ""
            }`}
            data-testid={`custom-league-row-${league.id}`}
        >
            <div className="flex items-start gap-3 flex-wrap">
                <div className={`w-10 h-10 flex items-center justify-center rounded-xl border shrink-0 ${
                    isCustom ? "bg-cyber/10 border-cyber/40" : "bg-black/40 border-white/[0.08]"
                }`}>
                    <Crown className={`w-5 h-5 ${isCustom ? "text-cyber" : "text-zinc-500"}`} />
                </div>
                <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-display font-black text-sm tracking-tight text-white">
                            {league.name}
                        </p>
                        {isCustom && (
                            <span
                                className="pill bg-cyber/15 border border-cyber/40 text-cyber text-[9px] py-0 px-1.5 uppercase"
                                data-testid={`custom-league-badge-${league.id}`}
                            >
                                Custom
                            </span>
                        )}
                    </div>
                    <p className="text-[11px] text-zinc-500 mt-0.5 flex items-center gap-2 flex-wrap">
                        <span className="font-mono bg-black/40 px-1.5 py-0.5 rounded border border-white/[0.06] text-zinc-300">
                            {league.invite_code || "—"}
                        </span>
                        <span className="font-mono text-zinc-500">
                            {league.admin_email || "no admin"}
                        </span>
                    </p>
                </div>
                <a
                    href={`/league/${league.id}`}
                    target="_blank"
                    rel="noreferrer"
                    className="brutal-btn-ghost text-xs py-1.5"
                    data-testid={`custom-league-open-${league.id}`}
                >
                    Open <ExternalLink className="w-3 h-3" />
                </a>
            </div>

            {/* Master toggle */}
            <label
                className={`mt-3 flex items-center gap-2 p-2 border-2 rounded cursor-pointer transition-colors ${
                    isCustom ? "border-cyber bg-cyber/5" : "border-[#333] bg-black/30 hover:border-[#555]"
                }`}
                data-testid={`custom-toggle-row-${league.id}`}
            >
                <span
                    className={`w-4 h-4 border-2 flex items-center justify-center flex-shrink-0 ${
                        isCustom ? "bg-cyber border-cyber" : "border-[#444] bg-transparent"
                    }`}
                    aria-hidden="true"
                >
                    {isCustom && <Check className="w-3 h-3 text-black" strokeWidth={3} />}
                </span>
                <input
                    type="checkbox"
                    className="hidden"
                    checked={isCustom}
                    disabled={saving}
                    onChange={() => patch({ custom_enabled: !isCustom })}
                    data-testid={`custom-toggle-checkbox-${league.id}`}
                />
                <span className="text-xs text-zinc-100 font-bold uppercase tracking-wide">
                    Custom Subscription Enabled
                </span>
                {saving && <Loader2 className="w-3.5 h-3.5 animate-spin text-cyber ml-auto" />}
            </label>

            {/* Per-integration toggles (only editable when master is on) */}
            <div className={`mt-2 grid sm:grid-cols-3 gap-2 ${isCustom ? "" : "opacity-40 pointer-events-none"}`}>
                {INTEGRATIONS.map(f => {
                    const on = !!local[f.key];
                    return (
                        <label
                            key={f.key}
                            className={`flex items-center gap-2 p-2 border-2 rounded cursor-pointer transition-colors ${
                                on ? "border-cyber/70 bg-cyber/5" : "border-[#333] bg-black/30 hover:border-[#555]"
                            }`}
                            data-testid={`custom-integration-${f.key}-row-${league.id}`}
                            title={f.help}
                        >
                            <span
                                className={`w-4 h-4 border-2 flex items-center justify-center flex-shrink-0 ${
                                    on ? "bg-cyber border-cyber" : "border-[#444] bg-transparent"
                                }`}
                                aria-hidden="true"
                            >
                                {on && <Check className="w-3 h-3 text-black" strokeWidth={3} />}
                            </span>
                            <input
                                type="checkbox"
                                className="hidden"
                                checked={on}
                                disabled={saving || !isCustom}
                                onChange={() => patch({ [f.key]: !on })}
                                data-testid={`custom-integration-${f.key}-checkbox-${league.id}`}
                            />
                            <span className="text-xs text-zinc-200 leading-tight">{f.label}</span>
                        </label>
                    );
                })}
            </div>
        </div>
    );
}


export default function CustomSubscriptionsTab() {
    const [q, setQ] = useState("");
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showAll, setShowAll] = useState(false);  // iter87.86

    const load = useCallback(async (query) => {
        setLoading(true);
        try {
            // iter87.86 — Default cap: 20 rows. "Show all" bumps to 200
            // (matches the backend endpoint cap).
            const r = await api.siteListCustomLeagues(query, showAll ? 200 : 20);
            setItems(r.items || []);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to load leagues");
        }
        setLoading(false);
    }, [showAll]);

    useEffect(() => {
        const t = setTimeout(() => load(q), 250);
        return () => clearTimeout(t);
    }, [q, load]);

    const customCount = items.filter(l => l.custom_enabled).length;

    return (
        <div className="space-y-4" data-testid="custom-subscriptions-tab">
            {/* Hero */}
            <div className="brutal-card p-5 flex flex-wrap items-center gap-4" data-testid="custom-subscriptions-hero">
                <div className="w-12 h-12 flex items-center justify-center bg-cyber/10 border border-cyber/40 rounded-xl shrink-0">
                    <Crown className="w-6 h-6 text-cyber" />
                </div>
                <div className="min-w-0 flex-1">
                    <h1 className="font-display font-black text-2xl sm:text-3xl uppercase tracking-tight leading-none">
                        Custom <span className="text-cyber">Subscriptions</span>
                    </h1>
                    <p className="text-[11px] text-zinc-500 mt-1.5 uppercase tracking-widest">
                        Per-rivalry integration grants · search · toggle · GotSport / CSV / Portfolio
                    </p>
                </div>
                <div className="flex items-center gap-3 text-[11px] uppercase tracking-widest">
                    <span className="text-zinc-500">
                        <span className="text-cyber font-black text-xl mr-1 font-display">{customCount}</span>
                        Custom
                    </span>
                    <span className="text-zinc-500">
                        <span className="text-white font-black text-xl mr-1 font-display">{items.length}</span>
                        Shown
                    </span>
                </div>
            </div>

            {/* Search */}
            <div className="brutal-card p-3 flex items-center gap-3 bg-black/40 backdrop-blur border-white/[0.06]">
                <div className="flex-1 relative">
                    <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
                    <input
                        type="text"
                        value={q}
                        onChange={e => setQ(e.target.value)}
                        placeholder="Search rivalries by name, invite code, or admin email…"
                        className="brutal-input w-full pl-9 text-sm"
                        data-testid="custom-subscriptions-search-input"
                    />
                </div>
                {loading && <Loader2 className="w-4 h-4 text-cyber animate-spin" />}
            </div>

            {/* Info banner */}
            <div className="brutal-card-light p-3 flex items-start gap-2 border-cyber/20 bg-cyber/[0.03]">
                <Sparkles className="w-4 h-4 text-cyber shrink-0 mt-0.5" />
                <p className="text-[11px] text-zinc-400 leading-relaxed">
                    <strong className="text-cyber">Custom is a per-rivalry grant, not a tier.</strong> Any rivalry can be granted custom access regardless of its tier (free / comp / paid). Turning the master switch off force-offs all three integrations for that rivalry.
                </p>
            </div>

            {/* Rows */}
            {items.length === 0 && !loading && (
                <div className="brutal-card p-8 text-center border-dashed">
                    <Trophy className="w-6 h-6 text-cyber/50 mx-auto mb-2" />
                    <p className="text-zinc-400 text-sm">
                        {q ? "No rivalries match your search." : "No rivalries yet."}
                    </p>
                </div>
            )}
            <div className="space-y-3">
                {items.map(lg => (
                    <CustomLeagueRow
                        key={lg.id}
                        league={lg}
                        onChange={(updated) => {
                            setItems(prev => prev.map(x => x.id === updated.id ? { ...x, ...updated } : x));
                        }}
                    />
                ))}
            </div>

            {/* iter87.86 — "Show all" toggle when we may have more than 20. */}
            {items.length >= 20 && (
                <button
                    type="button"
                    onClick={() => setShowAll(v => !v)}
                    className="brutal-btn-ghost w-full text-xs py-2.5 mt-1"
                    data-testid="custom-subs-show-all-btn"
                >
                    {showAll
                        ? `Collapse to 20 most recent`
                        : `Show all rivalries (up to 200)`}
                </button>
            )}
        </div>
    );
}
